import { useMemo, useState } from 'react';
import {
  bdrRows,
  formatBdrValue,
  type BdrColumn,
  type BdrLine,
  type BdrPeriodView,
} from '@entities/economic/bdrV2';
import { CellButton, Grid, NameCell, TableBox, Toggle } from './styles';

/**
 * Сетка периода: статьи «БДР сводной» деревом (разделы жирным, можно
 * свернуть до разделов — укрупнённый вид), колонки — объекты и центры
 * затрат, справа «Итого». «Нет данных» видно в ячейке, ручная правка —
 * полосой слева. Ячейка открывает строку: откуда сумма и правка.
 */
export function BdrTable({
  view,
  collapsedAll,
  onOpen,
}: {
  view: BdrPeriodView;
  collapsedAll: boolean;
  onOpen: (line: BdrLine, column: BdrColumn) => void;
}) {
  const rows = useMemo(() => bdrRows(view.articles), [view.articles]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const lines = useMemo(() => {
    const m = new Map<string, BdrLine>();
    for (const l of view.lines) m.set(`${l.article_code}|${l.column_key}`, l);
    return m;
  }, [view.lines]);
  const parents = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const a of view.articles) m.set(a.code, a.parent_code);
    return m;
  }, [view.articles]);

  // Строка скрыта, если свёрнут кто-то из предков (или всё свёрнуто до разделов).
  const hidden = (code: string, depth: number) => {
    if (collapsedAll && depth > 0) return true;
    let p = parents.get(code) ?? null;
    while (p) {
      if (collapsed.has(p)) return true;
      p = parents.get(p) ?? null;
    }
    return false;
  };

  function toggle(code: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  return (
    <TableBox>
      <Grid>
        <thead>
          <tr>
            <th scope="col">Статья</th>
            {view.columns.map((c) => (
              <th key={c.key} scope="col" title={c.label}>
                {c.label}
              </th>
            ))}
            <th scope="col">Итого</th>
          </tr>
        </thead>
        <tbody>
          {rows
            .filter((r) => !hidden(r.article.code, r.depth))
            .map(({ article: a, depth, hasChildren }) => {
              const total = a.kind === 'total' || hasChildren;
              return (
                <tr key={a.code} className={total ? 'total' : undefined}>
                  <NameCell className="name" $depth={depth}>
                    <span className="row">
                      {hasChildren && !collapsedAll ? (
                        <Toggle
                          type="button"
                          aria-expanded={!collapsed.has(a.code)}
                          aria-label={
                            collapsed.has(a.code) ? `Раскрыть «${a.name}»` : `Свернуть «${a.name}»`
                          }
                          onClick={() => toggle(a.code)}
                        >
                          {collapsed.has(a.code) ? '▸' : '▾'}
                        </Toggle>
                      ) : null}
                      {a.template_row && <span className="no">{a.template_row}</span>}
                      <span>{a.name}</span>
                    </span>
                  </NameCell>
                  {view.columns.map((c) => {
                    const l = lines.get(`${a.code}|${c.key}`);
                    if (!l) return <td key={c.key} />;
                    const cls = [
                      'cell',
                      l.no_data && l.manual_amount === null ? 'nodata' : '',
                      l.manual_amount !== null ? 'manual' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <td key={c.key} className={cls}>
                        <CellButton
                          type="button"
                          title={
                            l.hint ?? (l.manual_reason ? `Правка: ${l.manual_reason}` : undefined)
                          }
                          onClick={() => onOpen(l, c)}
                        >
                          {l.no_data && l.manual_amount === null
                            ? 'нет данных'
                            : formatBdrValue(l.amount, a.unit) || '0'}
                        </CellButton>
                      </td>
                    );
                  })}
                  <td className="totalcol">
                    {formatBdrValue(view.totals[a.code] ?? null, a.unit)}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </Grid>
    </TableBox>
  );
}
